import { applyMergePatch } from './mergePatch';

/**
 * WebSocket client of contracts/realtime.md: one connection per tab, reference-counted topic
 * subscriptions, snapshot + JSON-merge-patch deltas, event topics, sequence-gap and reconnect
 * `resume`, heartbeats with per-topic `as_of`, stale detection and backoff reconnects.
 */

export type RtStatus = 'idle' | 'connecting' | 'open' | 'reconnecting' | 'unauthenticated';

export interface RtEvent<T = any> {
  topic: string;
  seq: number;
  ts: string;
  data: T;
}

export interface TopicView<T = any> {
  /** current state (snapshot with deltas applied); for event topics the snapshot */
  data: T | undefined;
  /** a snapshot arrived and nothing is pending */
  ready: boolean;
  /** stale: no fresh source data for `staleAfterMs`, or not connected */
  stale: boolean;
  /** subscription denied or ended by the server (permission_denied, not_found, invalid_argument) */
  error: string | null;
  /** events received since the last snapshot (event topics), oldest first, bounded */
  events: RtEvent[];
  seq: number;
}

interface Entry {
  refs: number;
  data: any;
  ready: boolean;
  seq: number;
  error: string | null;
  events: RtEvent[];
  asOf: number;
  lastAt: number;
  view: TopicView;
  listeners: Set<() => void>;
  eventListeners: Set<(e: RtEvent) => void>;
  sent: boolean;
}

export interface RealtimeOptions {
  url?: () => string;
  WebSocketImpl?: typeof WebSocket;
  now?: () => number;
  staleAfterMs?: number;
  pingEveryMs?: number;
  maxBackoffMs?: number;
  /** keep at most this many events per topic in the view */
  maxEvents?: number;
}

const MAX_TOPICS_PER_MESSAGE = 50;

export class RealtimeClient {
  private ws: WebSocket | null = null;
  private topics = new Map<string, Entry>();
  private status: RtStatus = 'idle';
  private statusListeners = new Set<(s: RtStatus) => void>();
  private authListeners = new Set<() => void>();
  private lastMessageAt = 0;
  private backoff = 1000;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private staleTimer: ReturnType<typeof setInterval> | null = null;
  private idSeq = 0;
  private wanted = false;
  private opts: Required<RealtimeOptions>;

  constructor(opts: RealtimeOptions = {}) {
    this.opts = {
      url: opts.url || (() => (location.protocol === 'https:' ? 'wss://' : 'ws://') + location.host + '/ws'),
      WebSocketImpl: opts.WebSocketImpl || (globalThis.WebSocket as typeof WebSocket),
      now: opts.now || (() => Date.now()),
      staleAfterMs: opts.staleAfterMs ?? 10_000,
      pingEveryMs: opts.pingEveryMs ?? 25_000,
      maxBackoffMs: opts.maxBackoffMs ?? 30_000,
      maxEvents: opts.maxEvents ?? 200,
    };
  }

  // ---------- public API ----------

  /** Subscribe to a topic; returns the unsubscribe function. Connects on the first subscription. */
  subscribe(topic: string, onChange: () => void): () => void {
    const e = this.entry(topic);
    e.refs++;
    e.listeners.add(onChange);
    if (e.refs === 1) this.sendSubscribe([topic]);
    this.ensureConnected();
    return () => {
      e.listeners.delete(onChange);
      e.refs--;
      if (e.refs <= 0) {
        this.topics.delete(topic);
        if (this.isOpen() && e.sent) this.send({ op: 'unsubscribe', id: this.nextId(), topics: [topic] });
      }
    };
  }

  /** Listen to `event` messages of an event topic (alerts, cases). Requires a subscription. */
  onEvent(topic: string, fn: (e: RtEvent) => void): () => void {
    const e = this.entry(topic);
    e.eventListeners.add(fn);
    return () => e.eventListeners.delete(fn);
  }

  view<T = any>(topic: string): TopicView<T> {
    return (this.topics.get(topic)?.view as TopicView<T>) || EMPTY;
  }

  getStatus(): RtStatus {
    return this.status;
  }

  onStatus(fn: (s: RtStatus) => void): () => void {
    this.statusListeners.add(fn);
    return () => this.statusListeners.delete(fn);
  }

  /** Called when the server closes with 4401 (no or expired session). */
  onUnauthenticated(fn: () => void): () => void {
    this.authListeners.add(fn);
    return () => this.authListeners.delete(fn);
  }

  /** (Re)connect, e.g. after signing in. */
  connect() {
    this.wanted = true;
    if (this.status === 'unauthenticated') this.setStatus('idle');
    this.ensureConnected(true);
  }

  /** Close the connection (sign out); subscriptions are kept and resume on connect(). */
  disconnect() {
    this.wanted = false;
    this.clearTimers();
    const ws = this.ws;
    this.ws = null;
    if (ws) {
      ws.onclose = null;
      ws.close(1000);
    }
    this.setStatus('idle');
    this.markAll();
  }

  /** Debug/test: drop the connection as if the network failed (reconnects with resume). */
  dropConnection() {
    this.ws?.close(4000, 'dropped');
  }

  // ---------- internals ----------

  private entry(topic: string): Entry {
    let e = this.topics.get(topic);
    if (!e) {
      e = {
        refs: 0,
        data: undefined,
        ready: false,
        seq: 0,
        error: null,
        events: [],
        asOf: 0,
        lastAt: 0,
        view: EMPTY,
        listeners: new Set(),
        eventListeners: new Set(),
        sent: false,
      };
      this.topics.set(topic, e);
    }
    return e;
  }

  private isOpen() {
    return !!this.ws && this.ws.readyState === 1;
  }

  private nextId() {
    return 'c' + ++this.idSeq;
  }

  private send(msg: unknown) {
    if (this.isOpen()) this.ws!.send(JSON.stringify(msg));
  }

  private sendSubscribe(topics: string[]) {
    if (!this.isOpen()) return;
    for (let i = 0; i < topics.length; i += MAX_TOPICS_PER_MESSAGE) {
      const chunk = topics.slice(i, i + MAX_TOPICS_PER_MESSAGE);
      chunk.forEach((t) => (this.entry(t).sent = true));
      this.send({ op: 'subscribe', id: this.nextId(), topics: chunk });
    }
  }

  private sendResume(seqs: Record<string, number>) {
    const keys = Object.keys(seqs);
    for (let i = 0; i < keys.length; i += MAX_TOPICS_PER_MESSAGE) {
      const t: Record<string, number> = {};
      keys.slice(i, i + MAX_TOPICS_PER_MESSAGE).forEach((k) => {
        t[k] = seqs[k];
        this.entry(k).sent = true;
      });
      this.send({ op: 'resume', id: this.nextId(), topics: t });
    }
  }

  private ensureConnected(force = false) {
    if (!force && !this.wanted && this.topics.size === 0) return;
    this.wanted = true;
    if (this.ws || this.status === 'unauthenticated' || this.reconnectTimer) return;
    this.open();
  }

  private open() {
    this.setStatus(this.status === 'idle' ? 'connecting' : 'reconnecting');
    let ws: WebSocket;
    try {
      ws = new this.opts.WebSocketImpl(this.opts.url());
    } catch {
      this.scheduleReconnect();
      return;
    }
    this.ws = ws;
    ws.onopen = () => {
      this.lastMessageAt = this.opts.now();
      this.setStatus('open');
      const resume: Record<string, number> = {};
      const fresh: string[] = [];
      for (const [t, e] of this.topics) {
        e.sent = false;
        if (e.error) continue;
        if (e.seq > 0) resume[t] = e.seq;
        else fresh.push(t);
      }
      if (Object.keys(resume).length) this.sendResume(resume);
      if (fresh.length) this.sendSubscribe(fresh);
      this.startTimers();
    };
    ws.onmessage = (m) => this.onMessage(m.data);
    ws.onclose = (ev) => {
      this.ws = null;
      this.clearTimers();
      this.markAll();
      if (ev.code === 4401) {
        this.setStatus('unauthenticated');
        this.authListeners.forEach((f) => f());
        return;
      }
      if (!this.wanted) {
        this.setStatus('idle');
        return;
      }
      this.scheduleReconnect();
    };
    ws.onerror = () => {
      /* onclose follows */
    };
  }

  private scheduleReconnect() {
    this.setStatus('reconnecting');
    const delay = Math.min(this.backoff, this.opts.maxBackoffMs);
    this.backoff = Math.min(this.backoff * 2, this.opts.maxBackoffMs);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (this.wanted && !this.ws) this.open();
    }, delay);
  }

  private startTimers() {
    this.clearTimers(false);
    this.pingTimer = setInterval(() => this.send({ op: 'ping', id: this.nextId() }), this.opts.pingEveryMs);
    this.staleTimer = setInterval(() => this.markAll(), 1000);
  }

  private clearTimers(all = true) {
    if (this.pingTimer) clearInterval(this.pingTimer);
    if (this.staleTimer) clearInterval(this.staleTimer);
    this.pingTimer = this.staleTimer = null;
    if (all && this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private onMessage(raw: unknown) {
    let m: any;
    try {
      m = JSON.parse(String(raw));
    } catch {
      return;
    }
    const now = this.opts.now();
    this.lastMessageAt = now;
    switch (m.type) {
      case 'ack': {
        this.backoff = 1000;
        for (const d of m.denied || []) {
          const e = this.topics.get(d.topic);
          if (e) {
            e.error = d.code || 'permission_denied';
            this.publish(e);
          }
        }
        return;
      }
      case 'snapshot': {
        const e = this.topics.get(m.topic);
        if (!e) return;
        e.data = m.data;
        e.seq = m.seq ?? 0;
        e.ready = true;
        e.error = null;
        e.events = [];
        e.lastAt = now;
        e.asOf = now;
        this.publish(e);
        return;
      }
      case 'delta': {
        const e = this.topics.get(m.topic);
        if (!e) return;
        if (!e.ready || m.seq !== e.seq + 1) {
          this.sendResume({ [m.topic]: e.seq });
          return;
        }
        e.data = applyMergePatch(e.data, m.data);
        e.seq = m.seq;
        e.lastAt = now;
        e.asOf = now;
        this.publish(e);
        return;
      }
      case 'event': {
        const e = this.topics.get(m.topic);
        if (!e) return;
        if (e.ready && m.seq !== e.seq + 1) {
          this.sendResume({ [m.topic]: e.seq });
          return;
        }
        e.seq = m.seq;
        e.lastAt = now;
        const ev: RtEvent = { topic: m.topic, seq: m.seq, ts: m.ts, data: m.data };
        e.events = [...e.events, ev].slice(-this.opts.maxEvents);
        e.eventListeners.forEach((f) => f(ev));
        this.publish(e);
        return;
      }
      case 'reset': {
        const e = this.topics.get(m.topic);
        if (!e) return;
        e.ready = false;
        e.seq = 0;
        e.events = [];
        this.publish(e);
        return;
      }
      case 'heartbeat': {
        for (const [t, ts] of Object.entries((m.as_of || {}) as Record<string, string>)) {
          const e = this.topics.get(t);
          const at = Date.parse(ts);
          if (e && Number.isFinite(at)) e.asOf = at;
        }
        this.markAll();
        return;
      }
      case 'error': {
        if (!m.topic) return;
        const e = this.topics.get(m.topic);
        if (e) {
          e.error = m.code || 'internal';
          e.ready = false;
          this.publish(e);
        }
        return;
      }
      default:
        return;
    }
  }

  private isStale(e: Entry): boolean {
    if (!e.ready) return false;
    if (this.status !== 'open') return true;
    const now = this.opts.now();
    if (now - this.lastMessageAt > this.opts.staleAfterMs) return true;
    return e.asOf > 0 && now - e.asOf > this.opts.staleAfterMs;
  }

  private publish(e: Entry) {
    e.view = { data: e.data, ready: e.ready, stale: this.isStale(e), error: e.error, events: e.events, seq: e.seq };
    e.listeners.forEach((f) => f());
  }

  /** re-evaluate staleness of every topic; notify only those that changed */
  private markAll() {
    for (const e of this.topics.values()) {
      const s = this.isStale(e);
      if (s !== e.view.stale) this.publish(e);
    }
  }

  private setStatus(s: RtStatus) {
    if (s === this.status) return;
    this.status = s;
    this.statusListeners.forEach((f) => f(s));
  }
}

const EMPTY: TopicView = { data: undefined, ready: false, stale: false, error: null, events: [], seq: 0 };

/** The panel's single connection. */
export const realtime = new RealtimeClient();
